import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '../ui/badge';
import { AddressAutocomplete } from '../AddressAutocomplete';
import { useTheme } from '../../contexts/ThemeContext';
import { getMapThemeOverride, setMapThemeOverride } from '../../utils/mapTheme';
import { NOMINATIM_BASE } from '../../utils/nominatim';
import { APIProvider, Map as GoogleMapComponent, AdvancedMarker, AdvancedMarkerAnchorPoint, InfoWindow as GInfoWindow } from '@vis.gl/react-google-maps';
import { useMapStaleGuard } from '../../hooks/useMapStaleGuard';
import {
  GoogleMapController, GooglePolygon, GoogleCircle, GooglePolyline, TerraDrawManager, GoogleIdleListener,
} from '../maps/GoogleMapsWrappers';
import {
  MapContainer, TileLayer, Polygon as LeafletPolygon, Circle as LeafletCircle, Polyline as LeafletPolyline,
  Marker as LeafletMarker, Popup, useMapEvents, useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import { Map as MapIcon, Satellite, Mountain, ChevronDown, ChevronUp } from 'lucide-react';
import {
  PlaceMarkerContent,
  createPlaceIcon,
  getZoneCoords,
  getPlaceColor,
  getRouteCorridor,
} from './GeofencingUtils';
import { placeIconUrl } from './PlaceIconPicker';

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID || 'DEMO_MAP_ID';
const GOOGLE_MAPS_MAP_ID_NO_POI = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID_NO_POI || GOOGLE_MAPS_MAP_ID;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: '/leaflet/marker-icon-2x.png',
  iconUrl: '/leaflet/marker-icon.png',
  shadowUrl: '/leaflet/marker-shadow.png',
});

const LeafletMapController = ({ onMapReady }) => {
  const map = useMap();

  useEffect(() => {
    if (onMapReady) onMapReady(map);
    return () => { if (onMapReady) onMapReady(null); };
  }, [map, onMapReady]);

  return null;
};

const LeafletViewportTracker = ({ onViewportChange }) => {
  const map = useMap();

  useEffect(() => {
    if (!onViewportChange) return undefined;

    const syncViewport = () => {
      const center = map.getCenter();
      onViewportChange({
        center: { lat: center.lat, lng: center.lng },
        zoom: map.getZoom(),
      });
    };

    map.on('moveend', syncViewport);
    map.on('zoomend', syncViewport);
    return () => {
      map.off('moveend', syncViewport);
      map.off('zoomend', syncViewport);
    };
  }, [map, onViewportChange]);

  return null;
};

const LeafletMapSync = ({ center, zoom }) => {
  const map = useMap();
  const hasInitRef = useRef(false);

  useEffect(() => {
    if (!hasInitRef.current) {
      hasInitRef.current = true;
      map.setView([center.lat, center.lng], zoom, { animate: false });
    }
  }, [center, zoom, map]);

  return null;
};

const LeafletInvalidateSize = ({ trigger }) => {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      if (map) map.invalidateSize({ animate: false });
    }, 300);

    return () => clearTimeout(timer);
  }, [trigger, map]);

  return null;
};

const LocationPicker = ({ isSelecting, onLocationSelected }) => {
  useMapEvents({
    click(event) {
      if (!isSelecting) return;
      onLocationSelected([event.latlng.lat, event.latlng.lng]);
    },
  });

  return null;
};

const createLeafletEditLayer = (editTarget, color) => {
  if (!editTarget) return null;

  if (editTarget.type === 'circle' && editTarget.center) {
    return L.circle(editTarget.center, {
      radius: editTarget.radius || 500,
      color,
      fillColor: color,
      fillOpacity: 0.3,
      weight: 2,
    });
  }

  if (editTarget.type === 'polyline') {
    return L.polyline(editTarget.coordinates || [], {
      color,
      weight: 4,
    });
  }

  return L.polygon(editTarget.coordinates || [], {
    color,
    fillColor: color,
    fillOpacity: 0.3,
    weight: 2,
  });
};

const extractLeafletLayerShape = (layer, originalType) => {
  if (originalType === 'circle' && layer instanceof L.Circle) {
    const center = layer.getLatLng();
    return { type: 'circle', center: [center.lat, center.lng], radius: layer.getRadius() };
  }

  if (originalType === 'polyline' && layer instanceof L.Polyline && !(layer instanceof L.Polygon)) {
    return { type: 'polyline', coordinates: layer.getLatLngs().map((latLng) => [latLng.lat, latLng.lng]) };
  }

  const latlngs = layer.getLatLngs()[0] || layer.getLatLngs();
  return { type: originalType || 'polygon', coordinates: latlngs.map((latLng) => [latLng.lat, latLng.lng]) };
};

const GeomanController = ({
  isDrawing,
  drawShape,
  drawColor,
  onShapeCreated,
  editMode,
  editTarget,
  editColor,
  onShapeEdited,
  editInteractionMode,
}) => {
  const map = useMap();
  const isDrawingRef = useRef(false);
  const editLayerRef = useRef(null);
  const restoreDoubleClickTimerRef = useRef(null);
  const onShapeEditedRef = useRef(onShapeEdited);
  useEffect(() => { onShapeEditedRef.current = onShapeEdited; });

  const scheduleDoubleClickRestore = useCallback(() => {
    if (restoreDoubleClickTimerRef.current) clearTimeout(restoreDoubleClickTimerRef.current);
    restoreDoubleClickTimerRef.current = setTimeout(() => {
      if (map && !isDrawing && !editMode) map.doubleClickZoom.enable();
    }, 250);
  }, [editMode, isDrawing, map]);

  useEffect(() => {
    if (!map || !map.pm) return undefined;

    map.pm.addControls({
      position: 'topleft',
      drawMarker: false,
      drawCircleMarker: false,
      drawPolyline: false,
      drawRectangle: false,
      drawPolygon: false,
      drawCircle: false,
      drawText: false,
      editMode: false,
      dragMode: false,
      cutPolygon: false,
      removalMode: false,
      rotateMode: false,
    });
    map.pm.removeControls();
    map.pm.setGlobalOptions({ snappable: true, snapDistance: 15 });
    return undefined;
  }, [map]);

  useEffect(() => {
    if (!map || !map.pm) return undefined;

    if (isDrawing && drawShape) {
      map.doubleClickZoom.disable();
      const options = {
        snappable: true,
        snapDistance: 15,
        templineStyle: { color: drawColor, weight: 2 },
        hintlineStyle: { color: drawColor, weight: 2, dashArray: '5,5' },
        pathOptions: { color: drawColor, fillColor: drawColor, fillOpacity: 0.3, weight: 2 },
      };
      const shapeMap = { polygon: 'Polygon', rectangle: 'Rectangle', circle: 'Circle', polyline: 'Line' };
      const geomanShape = shapeMap[drawShape];
      if (geomanShape) {
        map.pm.enableDraw(geomanShape, options);
        isDrawingRef.current = true;
      }
    } else if (isDrawingRef.current) {
      map.pm.disableDraw();
      isDrawingRef.current = false;
      if (!editMode) scheduleDoubleClickRestore();
    }

    return undefined;
  }, [drawColor, drawShape, editMode, isDrawing, map, scheduleDoubleClickRestore]);

  useEffect(() => {
    if (!map) return undefined;

    const handler = (event) => {
      const { layer, shape } = event;
      let result;

      if (shape === 'Circle') {
        const center = layer.getLatLng();
        result = { type: 'circle', center: [center.lat, center.lng], radius: layer.getRadius() };
      } else if (shape === 'Line') {
        result = { type: 'polyline', coordinates: layer.getLatLngs().map((latLng) => [latLng.lat, latLng.lng]) };
      } else {
        const latlngs = layer.getLatLngs()[0] || layer.getLatLngs();
        result = { type: shape.toLowerCase(), coordinates: latlngs.map((latLng) => [latLng.lat, latLng.lng]) };
      }

      map.removeLayer(layer);
      onShapeCreated?.(result);
      scheduleDoubleClickRestore();
    };

    map.on('pm:create', handler);
    return () => map.off('pm:create', handler);
  }, [map, onShapeCreated, scheduleDoubleClickRestore]);

  // Effect 1: Edit layer lifecycle — create/destroy the editable shape on the map
  useEffect(() => {
    if (!map || !map.pm) return undefined;

    if (editLayerRef.current) {
      const existingLayer = editLayerRef.current;
      existingLayer.off();
      if (existingLayer.pm) {
        existingLayer.pm.disable();
        existingLayer.pm.disableLayerDrag();
      }
      if (map.hasLayer(existingLayer)) map.removeLayer(existingLayer);
      editLayerRef.current = null;
    }

    if (!editMode || !editTarget) {
      if (!isDrawing) scheduleDoubleClickRestore();
      return undefined;
    }

    map.doubleClickZoom.disable();
    const layer = createLeafletEditLayer(editTarget, editColor);
    if (!layer) return undefined;

    layer.addTo(map);
    editLayerRef.current = layer;

    const syncShape = () => {
      onShapeEditedRef.current?.(extractLeafletLayerShape(layer, editTarget.type));
    };

    layer.on('pm:edit', syncShape);
    layer.on('pm:dragend', syncShape);
    layer.on('pm:markerdragend', syncShape);

    return () => {
      layer.off('pm:edit', syncShape);
      layer.off('pm:dragend', syncShape);
      layer.off('pm:markerdragend', syncShape);
      if (layer.pm) {
        layer.pm.disable();
        layer.pm.disableLayerDrag();
      }
      if (map.hasLayer(layer)) map.removeLayer(layer);
      editLayerRef.current = null;
      if (!isDrawing) scheduleDoubleClickRestore();
    };
  }, [editColor, editMode, editTarget, isDrawing, map, scheduleDoubleClickRestore]);

  // Effect 2: Toggle between Reshape (vertex editing) and Move (layer drag) on the existing layer
  useEffect(() => {
    const layer = editLayerRef.current;
    if (!layer || !layer.pm || !editMode) return;

    if (editInteractionMode === 'move') {
      if (layer.pm.enabled()) layer.pm.disable();
      layer.pm.enableLayerDrag();
    } else {
      layer.pm.disableLayerDrag();
      layer.pm.enable({
        snappable: true,
        snapDistance: 15,
        allowSelfIntersection: false,
      });
    }
  }, [editInteractionMode, editMode, editTarget]);

  useEffect(() => () => {
    if (restoreDoubleClickTimerRef.current) clearTimeout(restoreDoubleClickTimerRef.current);
  }, []);

  return null;
};

export const GeofencingMap = ({
  mapType,
  onMapTypeChange,
  hasOSMMaps,
  hasGoogleMaps,
  hasSatelliteView,
  hasTerrainView,
  hasDarkMode,
  mapCenter,
  mapZoom,
  mapRef,
  leafletMapRef,
  showPOI,
  onTogglePOI,
  isFullscreen,
  onToggleFullscreen,
  activePanel,
  editingZoneId,
  editingPlaceId,
  editingRouteId,
  isDrawingZone,
  onDrawingZoneChange,
  isDrawingRoute,
  onDrawingRouteChange,
  isSelectingLocation,
  onSelectingLocationChange,
  isEditingShape,
  editInteractionMode,
  onEditInteractionModeChange,
  zoneForm,
  onZoneGeometryChange,
  placeForm,
  onPlaceChange,
  routeForm,
  onRoutePathChange,
  zones,
  places,
  routes,
  selectedZone,
  selectedPlace,
  selectedRoute,
  showZonesLayer,
  showPlacesLayer,
  showRoutesLayer,
  infoWindowOpen,
  onInfoWindowOpenChange,
  addressSearch,
  onAddressSearchChange,
  searchMarker,
  onSearchMarkerChange,
  onSelectZone,
  onSelectPlace,
  onSelectRoute,
  onViewportChange,
}) => {
  const { isDark } = useTheme();
  const googleAnimatingRef = useRef(false);
  const onViewportChangeRef = useRef(onViewportChange);
  const [stableEditTarget, setStableEditTarget] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const gmapEpoch = useMapStaleGuard();

  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);

  useEffect(() => {
    if (mapType === 'osm' && leafletMapRef.current?._mapPane && leafletMapRef.current?._loaded) {
      const center = leafletMapRef.current.getCenter();
      const zoom = leafletMapRef.current.getZoom();
      if (center) {
        onViewportChangeRef.current?.({ center: { lat: center.lat, lng: center.lng }, zoom });
      }
    } else if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const zoom = mapRef.current.getZoom();
      if (center) {
        onViewportChangeRef.current?.({ center: { lat: center.lat(), lng: center.lng() }, zoom });
      }
    }


    // Restore the user's explicit map-mode choice (shared across the three map pages);
    // fall back to the master app theme only when they've never toggled a map.
    setDarkMode(hasDarkMode ? (getMapThemeOverride(isDark) ?? isDark) : false);
  }, [hasDarkMode, isDark, leafletMapRef, mapRef, mapType]);

  const buildEditTarget = useCallback(() => {
    if (activePanel === 'zone' && (zoneForm.geometry?.coordinates?.length >= 3 || zoneForm.geometry?.center)) {
      if (zoneForm.geometry?.type === 'circle') {
        return { type: 'circle', center: zoneForm.geometry.center, radius: zoneForm.geometry.radius || 500 };
      }
      return { type: zoneForm.geometry?.type || 'polygon', coordinates: zoneForm.geometry.coordinates || [] };
    }

    if (activePanel === 'place' && placeForm.coordinates?.length >= 2) {
      return { type: 'circle', center: placeForm.coordinates, radius: placeForm.radius || 100 };
    }

    if (activePanel === 'route' && routeForm.path_coordinates?.length >= 2) {
      return { type: 'polyline', coordinates: routeForm.path_coordinates };
    }

    return null;
  }, [activePanel, placeForm.coordinates, placeForm.radius, routeForm.path_coordinates, zoneForm.geometry]);

  const buildEditTargetRef = useRef(buildEditTarget);
  // Updated in an effect (not during render) to satisfy react-hooks/refs. The
  // consuming effect below is declared after this one, so on any commit this
  // assignment runs first and the consumer reads the fresh builder.
  useEffect(() => { buildEditTargetRef.current = buildEditTarget; });

  useEffect(() => {
    // Snapshot the edit target only when edit mode is (re)entered (or clear it on
    // leaving) — deliberately NOT when the live geometry changes, so the editable
    // Leaflet layer isn't torn down mid-drag. This snapshot-on-trigger genuinely
    // needs an effect; react-hooks/set-state-in-effect over-flags it.
    if (!isEditingShape) {

      setStableEditTarget(null);
      return;
    }
    setStableEditTarget(buildEditTargetRef.current());
  }, [activePanel, editingPlaceId, editingRouteId, editingZoneId, isEditingShape]);

  const handleAddressSelect = useCallback((selection) => {
    const lat = Number(selection.lat);
    const lng = Number(selection.lon);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return;

    if (mapType === 'osm' && leafletMapRef.current) {
      leafletMapRef.current.flyTo([lat, lng], 16);
    } else if (mapRef.current) {
      mapRef.current.panTo({ lat, lng });
      mapRef.current.setZoom(16);
    }

    onSearchMarkerChange({ lat, lng });
    toast.success('Location found');
  }, [leafletMapRef, mapRef, mapType, onSearchMarkerChange]);

  const handleGoogleIdle = useCallback(() => {
    const map = mapRef.current;
    if (!map || googleAnimatingRef.current) return;
    const center = map.getCenter();
    const zoom = map.getZoom();
    if (center) {
      onViewportChange?.({
        center: { lat: center.lat(), lng: center.lng() },
        zoom,
      });
    }
    googleAnimatingRef.current = false;
  }, [mapRef, onViewportChange]);

  const [legendOpen, setLegendOpen] = useState(false);
  const routeCorridors = useMemo(() => {
    const m = {};
    routes.forEach((r) => { m[r.id] = getRouteCorridor(r.path_coordinates, r.deviation_distance); });
    return m;
  }, [routes]);
  const previewRouteCorridor = useMemo(() => (
    activePanel === 'route' ? getRouteCorridor(routeForm.path_coordinates, routeForm.deviation_distance) : null
  ), [activePanel, routeForm.path_coordinates, routeForm.deviation_distance]);
  const toGooglePaths = (polys) => (polys ? polys.map((rings) => rings.map((ring) => ring.map(([lat, lng]) => ({ lat, lng })))) : null);
  const routeCorridorsG = useMemo(() => {
    const m = {};
    Object.entries(routeCorridors).forEach(([id, polys]) => { m[id] = toGooglePaths(polys); });
    return m;
  }, [routeCorridors]);
  const previewRouteCorridorG = useMemo(() => toGooglePaths(previewRouteCorridor), [previewRouteCorridor]);
  const legendZones = showZonesLayer ? zones.filter((z) => z.visible_on_map) : [];
  const legendPlaces = showPlacesLayer ? places.filter((p) => p.visible_on_map) : [];
  const legendRoutes = showRoutesLayer ? routes.filter((r) => r.visible_on_map) : [];
  const legendCount = legendZones.length + legendPlaces.length + legendRoutes.length;

  return (
    <div className="h-full min-h-[24rem] flex-1 relative rounded-lg overflow-hidden border border-border lg:min-h-0" data-testid="geofencing-map-container">
      {hasOSMMaps && hasGoogleMaps ? (
        <div className="absolute top-[3.75rem] left-3 right-3 z-[1000] sm:top-4 sm:left-1/2 sm:right-auto sm:-translate-x-1/2" data-testid="map-provider-toggle">
          <div className="flex justify-center gap-2">
            <button
              onClick={() => onMapTypeChange('osm')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg ${mapType === 'osm' ? 'bg-[#2563eb] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-[#2563eb] hover:text-white'}`}
              data-testid="switch-to-osm-btn"
              type="button"
            >
              OSM
            </button>
            <button
              onClick={() => onMapTypeChange('roadmap')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all shadow-lg ${mapType !== 'osm' ? 'bg-[#2563eb] text-white' : 'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-[#2563eb] hover:text-white'}`}
              data-testid="switch-to-google-btn"
              type="button"
            >
              Google
            </button>
          </div>
        </div>
      ) : null}

      {hasGoogleMaps && mapType !== 'osm' ? (
        <div className="absolute top-[3.75rem] left-3 z-[1000] flex gap-1 sm:top-4 sm:left-4" data-testid="google-map-type-controls">
          <button onClick={() => onMapTypeChange('roadmap')} title="Street Map" className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${mapType === 'roadmap' ? 'bg-[#2563eb] text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'}`} data-testid="google-roadmap-btn" type="button">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="3 6 9 3 15 6 21 3 21 18 15 21 9 18 3 21" /><line x1="9" y1="3" x2="9" y2="18" /><line x1="15" y1="6" x2="15" y2="21" /></svg>
          </button>
          {hasSatelliteView ? (
            <button onClick={() => onMapTypeChange('satellite')} title="Satellite View" className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${mapType === 'satellite' ? 'bg-[#2563eb] text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'}`} data-testid="google-satellite-btn" type="button">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16.201 7.79899C17.8024 9.40034 20.3987 9.40034 22 7.79899L16.201 2C14.5997 3.60135 14.5997 6.19764 16.201 7.79899Z" />
                <path d="M16 8L14.5 9.5" /><path d="M14.8322 13.001C15.4344 12.3988 15.7354 12.0977 15.7354 11.7236C15.7354 11.3494 15.4344 11.0484 14.8322 10.4462L13.5548 9.16879C12.9526 8.56662 12.6515 8.26553 12.2774 8.26553C11.9033 8.26553 11.6022 8.56662 11 9.16879L6.79367 13.3751C5.73544 14.4334 5.73544 16.1491 6.79367 17.2073C7.8519 18.2656 9.56763 18.2656 10.6259 17.2073L14.8322 13.001Z" />
                <path d="M15.4688 16.8563L16.8563 15.4688C17.5104 14.8147 17.8374 14.4877 18.2438 14.4877C18.6502 14.4877 18.9773 14.8147 19.6314 15.4688L21.0189 16.8563C21.673 17.5104 22 17.8374 22 18.2438C22 18.6502 21.673 18.9773 21.0189 19.6314L19.6314 21.0189C18.9773 21.673 18.6502 22 18.2438 22C17.8374 22 17.5104 21.673 16.8563 21.0189L15.4688 19.6314C14.8147 18.9773 14.4877 18.6502 14.4877 18.2438C14.4877 17.8374 14.8147 17.5104 15.4688 16.8563Z" />
                <path d="M2.98112 4.36864L4.36864 2.98112C5.02273 2.32704 5.34977 2 5.75616 2C6.16256 2 6.4896 2.32704 7.14368 2.98112L8.5312 4.36864C9.18528 5.02273 9.51233 5.34977 9.51233 5.75616C9.51233 6.16256 9.18528 6.4896 8.5312 7.14368L7.14368 8.5312C6.4896 9.18528 6.16256 9.51233 5.75616 9.51233C5.34977 9.51233 5.02273 9.18528 4.36864 8.5312L2.98112 7.14368C2.32704 6.4896 2 6.16256 2 5.75616C2 5.34977 2.32704 5.02273 2.98112 4.36864Z" />
                <path d="M16 16L14 14M9.99999 10L8 8" />
                <path d="m 3.6955907,16.837439 c 0.11884,1.5706 1.56973,3.0084 3.1517,3.1305 m -5.811874,-3.06153 c -0.15384005,3.0281 2.74794,5.9038 5.79835,5.7565" />
              </svg>
            </button>
          ) : null}
          {hasTerrainView && (mapType === 'roadmap' || mapType === 'terrain') ? (
            <button onClick={() => onMapTypeChange('terrain')} title="Terrain View" className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${mapType === 'terrain' ? 'bg-[#2563eb] text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'}`} data-testid="google-terrain-btn" type="button">
              <Mountain className="w-5 h-5" />
            </button>
          ) : null}
          {mapType === 'satellite' || mapType === 'hybrid' ? (
            <button onClick={() => onMapTypeChange('hybrid')} title="Hybrid View" className={`w-9 h-9 rounded-md transition-all flex items-center justify-center ${mapType === 'hybrid' ? 'bg-[#2563eb] text-white shadow-lg' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg'}`} data-testid="google-hybrid-btn" type="button">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
                <path d="M18.8295 14.2542C19.1276 13.9312 19.2766 13.7698 19.435 13.6756C19.8171 13.4483 20.2876 13.4412 20.6761 13.6569C20.8372 13.7463 20.9908 13.9032 21.298 14.2171C21.6053 14.531 21.7589 14.6879 21.8464 14.8524C22.0575 15.2492 22.0506 15.7299 21.8281 16.1203C21.7359 16.2821 21.5779 16.4343 21.2617 16.7388L17.5003 20.3617C16.9012 20.9387 16.6016 21.2273 16.2272 21.3735C15.8528 21.5197 15.4413 21.509 14.6181 21.4874L14.5062 21.4845C14.2556 21.478 14.1303 21.4747 14.0574 21.392C13.9846 21.3094 13.9945 21.1817 14.0144 20.9265L14.0252 20.7879C14.0812 20.0694 14.1092 19.7102 14.2495 19.3873C14.3898 19.0644 14.6318 18.8022 15.1158 18.2778L18.8295 14.2542Z" />
                <path d="M22 10.5V9.71749C22 7.77787 22 6.80807 21.4142 6.2055C20.8284 5.60294 19.8856 5.60294 18 5.60294H15.9214C15.004 5.60294 14.9964 5.10116 14.1715 5.18834L10.8399 3.52114C9.44884 2.82504 8.75332 2.47699 8.01238 2.50118C7.27143 2.52537 6.59877 2.91808 5.25345 3.70351L4.02558 4.42037C3.03739 4.99729 2.54329 5.28576 2.27164 5.76564C2 6.24553 2 6.82993 2 7.99873V16.2157C2 17.7514 2 18.5193 2.34226 18.9467C2.57001 19.231 2.88916 19.4222 3.242 19.4856C3.77226 19.5808 4.42148 19.2018 5.71987 18.4437C6.60156 17.929 7.45011 17.3944 8.50487 17.5394C9.38869 17.6608 10.21 18.2185 11 18.6138" />
                <path d="M8 2.5L8 17.5" /><path d="M15 5.5V13.5" />
              </svg>
            </button>
          ) : null}
        </div>
      ) : null}

      <div className="absolute top-[6.5rem] left-3 z-[1000] flex flex-col gap-2 sm:top-[80px] sm:left-4" data-testid="map-zoom-controls">
        <button onClick={() => { if (mapType === 'osm' && leafletMapRef.current) leafletMapRef.current.zoomIn(); else if (mapRef.current) mapRef.current.setZoom(mapRef.current.getZoom() + 1); }} title="Zoom in" className="w-9 h-9 bg-white dark:bg-slate-800 rounded-md shadow-lg hover:shadow-xl flex items-center justify-center transition-all border border-slate-300 dark:border-slate-600 cursor-pointer" data-testid="zoom-in-btn" type="button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /><line x1="11" y1="8" x2="11" y2="14" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
        </button>
        <button onClick={() => { if (mapType === 'osm' && leafletMapRef.current) leafletMapRef.current.zoomOut(); else if (mapRef.current) mapRef.current.setZoom(mapRef.current.getZoom() - 1); }} title="Zoom out" className="w-9 h-9 bg-white dark:bg-slate-800 rounded-md shadow-lg hover:shadow-xl flex items-center justify-center transition-all border border-slate-300 dark:border-slate-600 cursor-pointer" data-testid="zoom-out-btn" type="button">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /><line x1="8" y1="11" x2="14" y2="11" /></svg>
        </button>
      </div>

      {hasGoogleMaps && mapType !== 'osm' ? (
        <div className="absolute top-1/2 -translate-y-1/2 left-4 z-[1000] flex flex-col gap-2" data-testid="google-side-controls">
          {mapType !== 'satellite' ? (
            <button onClick={onTogglePOI} title="Toggle POI Labels" className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${showPOI ? 'bg-[#2563eb] text-white' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'}`} data-testid="toggle-poi-btn" type="button">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 10V9.21749C22 7.27787 22 6.30807 21.4142 5.7055C20.8284 5.10294 19.8856 5.10294 18 5.10294H15.9214C15.004 5.10294 14.9964 5.10116 14.1715 4.68834L10.8399 3.02114C9.44884 2.32504 8.75332 1.97699 8.01238 2.00118C7.27143 2.02537 6.59877 2.41808 5.25345 3.20351L4.02558 3.92037C3.03739 4.49729 2.54329 4.78576 2.27164 5.26564C2 5.74553 2 6.32993 2 7.49873V15.7157C2 17.2514 2 18.5193 2.34226 18.4467C2.57001 18.731 2.88916 18.9222 3.242 19.4856C3.77226 19.0808 4.42148 18.7018 5.71987 17.9437C6.60156 17.429 7.45011 16.8944 8.50487 17.0394C9.38869 17.6608 10.21 18.2185 11 18.1138" />
                <path d="M8 2L8 17" /><path d="M15 5V9.5" />
                <path d="M18.3083 21.6835C18.0915 21.8865 17.8017 22 17.5001 22C17.1985 22 16.9087 21.8865 16.6919 21.6835C14.7063 19.813 12.0455 17.7235 13.3431 14.6898C14.0447 13.0496 15.7289 12 17.5001 12C19.2713 12 20.9555 13.0496 21.6571 14.6898C22.9531 17.7196 20.2988 19.8194 18.3083 21.6835Z" />
                <path d="M17.5 16.5H17.509" />
              </svg>
            </button>
          ) : null}
          {hasDarkMode ? (
            <button
              onClick={() => {
                if (mapRef.current) {
                  const c = mapRef.current.getCenter();
                  const z = mapRef.current.getZoom();
                  if (c) onViewportChange?.({ center: { lat: c.lat(), lng: c.lng() }, zoom: z });
                }
                setDarkMode((prev) => {
                  const next = !prev;
                  setMapThemeOverride(next, isDark);
                  return next;
                });
              }}
              className={`w-9 h-9 rounded-md transition-all shadow-md hover:shadow-lg flex items-center justify-center ${darkMode ? 'bg-[#2563eb] text-white' : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300'}`}
              title={darkMode ? 'Switch to Light Map' : 'Switch to Dark Map'}
              data-testid="toggle-dark-mode-btn"
              type="button"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            </button>
          ) : null}
          <button onClick={onToggleFullscreen} title={isFullscreen ? 'Exit Full Screen' : 'Full Screen'} className="hidden w-9 h-9 rounded-md bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300 shadow-md hover:shadow-lg transition-all sm:flex items-center justify-center" data-testid="toggle-fullscreen-btn" type="button">
            {isFullscreen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" /></svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" /></svg>
            )}
          </button>
        </div>
      ) : null}

      {!(isDrawingZone || isDrawingRoute || isSelectingLocation || isEditingShape) ? (
        <div className="absolute top-3 left-3 right-3 z-[1000] sm:top-4 sm:left-auto sm:right-4" data-testid="map-address-search-wrap">
          <AddressAutocomplete
            value={addressSearch}
            onChange={onAddressSearchChange}
            onSelect={handleAddressSelect}
            onClear={() => onSearchMarkerChange(null)}
            placeholder="Search address..."
            inputClassName="h-9 w-full sm:w-56 text-base md:text-sm bg-white dark:bg-[#0D1425]"
            data-testid="geo-address-search"
          />
        </div>
      ) : (
        <div className="absolute top-4 right-4 z-[1000]" data-testid="map-mode-badge-wrap">
          <Badge className="bg-amber-500 text-white animate-pulse" data-testid="map-mode-badge">
            {isSelectingLocation ? 'Click to select location'
              : isEditingShape ? (
                mapType === 'osm'
                  ? editInteractionMode === 'move'
                    ? 'Drag the shape to reposition it'
                    : activePanel === 'place'
                      ? 'Drag center or radius handle to resize'
                      : 'Drag vertices to reshape the boundary'
                  : activePanel === 'place'
                    ? 'Drag the center or radius handle to edit the place'
                    : 'Drag vertices to reshape, or drag the shape to move'
              )
              : isDrawingZone && zoneForm.zone_type === 'rectangle' ? 'Click first corner, then click opposite corner'
                : isDrawingZone && zoneForm.zone_type === 'circle' ? 'Click center, then drag for radius'
                  : isDrawingRoute ? 'Click to add waypoints · Click last point to finish'
                    : 'Click to add points · Double-click to finish'}
          </Badge>
        </div>
      )}

      {isEditingShape && mapType === 'osm' && stableEditTarget?.type !== 'circle' && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000]" data-testid="edit-mode-toggle">
          <div className="flex items-center gap-1 bg-white dark:bg-slate-800 rounded-lg shadow-lg p-1 border border-slate-200 dark:border-slate-600">
            <button
              onClick={() => onEditInteractionModeChange('reshape')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${editInteractionMode === 'reshape' ? 'bg-[#2563eb] text-white shadow' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
              data-testid="edit-mode-reshape-btn"
              type="button"
            >
              Reshape
            </button>
            <button
              onClick={() => onEditInteractionModeChange('move')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${editInteractionMode === 'move' ? 'bg-[#2563eb] text-white shadow' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700'}`}
              data-testid="edit-mode-move-btn"
              type="button"
            >
              Move
            </button>
          </div>
        </div>
      )}

      {mapType === 'osm' ? (
        <MapContainer center={mapCenter} zoom={mapZoom} className="h-full w-full" scrollWheelZoom zoomControl={false} doubleClickZoom={!(isDrawingZone || isDrawingRoute || isEditingShape)}>
          <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <GeomanController
            isDrawing={isDrawingZone || isDrawingRoute}
            drawShape={isDrawingZone ? zoneForm.zone_type : isDrawingRoute ? 'polyline' : null}
            drawColor={isDrawingZone ? zoneForm.zone_color : routeForm.route_color}
            onShapeCreated={(result) => {
              if (isDrawingZone) {
                onZoneGeometryChange(result.type === 'circle' ? result : { type: result.type, coordinates: result.coordinates });
                onDrawingZoneChange(false);
                return;
              }

              if (isDrawingRoute) {
                onRoutePathChange(result.coordinates || []);
                onDrawingRouteChange(false);
              }
            }}
            editMode={isEditingShape}
            editInteractionMode={editInteractionMode}
            editTarget={stableEditTarget}
            editColor={activePanel === 'zone' ? zoneForm.zone_color : activePanel === 'place' ? '#10B981' : routeForm.route_color}
            onShapeEdited={(result) => {
              if (activePanel === 'zone') {
                onZoneGeometryChange(result.type === 'circle' ? result : { type: result.type, coordinates: result.coordinates });
              } else if (activePanel === 'place' && result.type === 'circle') {
                onPlaceChange((previous) => ({ ...previous, coordinates: result.center, radius: result.radius }));
              } else if (activePanel === 'route') {
                onRoutePathChange(result.coordinates || []);
              }
            }}
          />
          <LocationPicker
            isSelecting={isSelectingLocation}
            onLocationSelected={(coordinates) => {
              onPlaceChange((previous) => ({ ...previous, coordinates }));
              onSelectingLocationChange(false);
              toast.success('Location selected');
              fetch(`${NOMINATIM_BASE}/reverse?format=json&lat=${coordinates[0]}&lon=${coordinates[1]}&zoom=18&addressdetails=1`)
                .then((r) => r.ok ? r.json() : null)
                .then((data) => {
                  if (!data) return;
                  const a = data.address;
                  const parts = [];
                  const street = [a?.house_number, a?.road].filter(Boolean).join(' ');
                  if (street) parts.push(street);
                  const suburb = a?.suburb || a?.village || a?.town || a?.hamlet;
                  if (suburb) parts.push(suburb);
                  if (a?.state) parts.push(a.state);
                  if (a?.postcode) parts.push(a.postcode);
                  if (a?.country) parts.push(a.country);
                  const addr = parts.length ? parts.join(', ') : data.display_name || '';
                  if (addr) onPlaceChange((prev) => ({ ...prev, address: addr }));
                })
                .catch(() => {});
            }}
          />

          {activePanel === 'zone' && !isDrawingZone && !isEditingShape && zoneForm.geometry?.coordinates?.length >= 3 ? (
            <LeafletPolygon positions={zoneForm.geometry.coordinates} pathOptions={{ color: zoneForm.zone_color, fillColor: zoneForm.zone_color, fillOpacity: 0.3, weight: 2 }} />
          ) : null}
          {activePanel === 'zone' && !isDrawingZone && !isEditingShape && zoneForm.geometry?.type === 'circle' && zoneForm.geometry?.center ? (
            <LeafletCircle center={zoneForm.geometry.center} radius={zoneForm.geometry.radius || 500} pathOptions={{ color: zoneForm.zone_color, fillColor: zoneForm.zone_color, fillOpacity: 0.3, weight: 2 }} />
          ) : null}
          {activePanel === 'place' && !isSelectingLocation && !isEditingShape && placeForm.coordinates?.length >= 2 ? (
            <LeafletCircle center={placeForm.coordinates} radius={placeForm.radius || 100} pathOptions={{ color: getPlaceColor(placeForm.icon), fillColor: getPlaceColor(placeForm.icon), fillOpacity: 0.15, weight: 2, dashArray: '6,4' }} />
          ) : null}
          {activePanel === 'place' && !isSelectingLocation && !isEditingShape && placeForm.coordinates?.length >= 2 && placeForm.icon ? (
            <LeafletMarker position={placeForm.coordinates} icon={createPlaceIcon(placeForm.icon)} />
          ) : null}
          {activePanel === 'route' && !isDrawingRoute && !isEditingShape && previewRouteCorridor ? previewRouteCorridor.map((rings, i) => (
            <LeafletPolygon key={`preview-corridor-${i}`} positions={rings} pathOptions={{ color: routeForm.route_color, fillColor: routeForm.route_color, fillOpacity: 0.08, weight: 1.5, dashArray: '6,4' }} />
          )) : null}
          {activePanel === 'route' && !isDrawingRoute && !isEditingShape && routeForm.path_coordinates?.length >= 2 ? (
            <LeafletPolyline positions={routeForm.path_coordinates} pathOptions={{ color: routeForm.route_color, weight: 4 }} />
          ) : null}

          {showRoutesLayer ? routes.filter((route) => route.visible_on_map && editingRouteId !== route.id).flatMap((route) => (
            (routeCorridors[route.id] || []).map((rings, i) => (
              <LeafletPolygon key={`corridor-${route.id}-${i}`} positions={rings} pathOptions={{ color: route.route_color, fillColor: route.route_color, fillOpacity: selectedRoute?.id === route.id ? 0.14 : 0.08, weight: 1.5, dashArray: '6,4' }} eventHandlers={{ click: () => onSelectRoute(route, { focus: false }) }} />
            ))
          )) : null}

          {showZonesLayer ? zones.filter((zone) => zone.visible_on_map && editingZoneId !== zone.id).map((zone) => {
            const { type, coords, center, radius } = getZoneCoords(zone);
            return type === 'circle' && center ? (
              <LeafletCircle key={zone.id} center={center} radius={radius} pathOptions={{ color: zone.zone_color, fillColor: zone.zone_color, fillOpacity: selectedZone?.id === zone.id ? 0.4 : 0.2, weight: selectedZone?.id === zone.id ? 3 : 2 }} eventHandlers={{ click: () => onSelectZone(zone, { focus: false }) }} />
            ) : coords.length >= 3 ? (
              <LeafletPolygon key={zone.id} positions={coords} pathOptions={{ color: zone.zone_color, fillColor: zone.zone_color, fillOpacity: selectedZone?.id === zone.id ? 0.4 : 0.2, weight: selectedZone?.id === zone.id ? 3 : 2 }} eventHandlers={{ click: () => onSelectZone(zone, { focus: false }) }} />
            ) : null;
          }) : null}

          {showZonesLayer ? zones.filter((zone) => zone.visible_on_map && zone.show_name_on_map && editingZoneId !== zone.id).map((zone) => {
            const geometry = zone.geometry;
            let lat;
            let lng;
            if (geometry?.type === 'circle' && geometry.center) {
              lat = geometry.center[0];
              lng = geometry.center[1];
            } else if (geometry?.coordinates?.length > 0) {
              lat = geometry.coordinates.reduce((sum, coord) => sum + coord[0], 0) / geometry.coordinates.length;
              lng = geometry.coordinates.reduce((sum, coord) => sum + coord[1], 0) / geometry.coordinates.length;
            }
            if (lat == null || lng == null) return null;
            return (
              <LeafletMarker
                key={`label-${zone.id}`}
                position={[lat, lng]}
                icon={L.divIcon({
                  className: 'zone-label',
                  iconSize: [100, 20],
                  iconAnchor: [50, 10],
                  html: `<div style="background:${zone.zone_color};color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;white-space:nowrap;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${zone.zone_name}</div>`,
                })}
              />
            );
          }) : null}

          {showPlacesLayer ? places.filter((place) => place.visible_on_map && place.coordinates?.length >= 2 && editingPlaceId !== place.id).map((place) => (
            <LeafletCircle key={`radius-${place.id}`} center={place.coordinates} radius={place.radius || 100} pathOptions={{ color: getPlaceColor(place.icon), fillColor: getPlaceColor(place.icon), fillOpacity: selectedPlace?.id === place.id ? 0.25 : 0.12, weight: 2 }} eventHandlers={{ click: () => onSelectPlace(place, { focus: false }) }} />
          )) : null}

          {showPlacesLayer ? places.filter((place) => place.visible_on_map && place.icon && editingPlaceId !== place.id).map((place) => (
            <LeafletMarker key={place.id} position={place.coordinates} icon={createPlaceIcon(place.icon)} eventHandlers={{ click: () => onSelectPlace(place, { focus: false }) }}>
              <Popup>
                <div className="p-1">
                  <p className="font-semibold" data-testid={`place-popup-name-${place.id}`}>{place.place_name}</p>
                  {place.address ? <p className="text-xs">{place.address}</p> : null}
                </div>
              </Popup>
            </LeafletMarker>
          )) : null}

          {showPlacesLayer ? places.filter((place) => place.visible_on_map && place.show_name_on_map && editingPlaceId !== place.id).map((place) => (
            <LeafletMarker
              key={`label-${place.id}`}
              position={place.coordinates}
              icon={L.divIcon({
                className: 'place-label',
                iconSize: [100, 20],
                iconAnchor: [50, -5],
                html: `<div style="background:${getPlaceColor(place.icon)};color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;white-space:nowrap;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${place.place_name}</div>`,
              })}
            />
          )) : null}

          {showRoutesLayer ? routes.filter((route) => route.visible_on_map && editingRouteId !== route.id).map((route) => (
            <LeafletPolyline key={route.id} positions={route.path_coordinates} pathOptions={{ color: route.route_color, weight: selectedRoute?.id === route.id ? 5 : 3, opacity: selectedRoute?.id === route.id ? 1 : 0.7 }} eventHandlers={{ click: () => onSelectRoute(route, { focus: false }) }} />
          )) : null}

          {showRoutesLayer ? routes.filter((route) => route.visible_on_map && route.show_name_on_map && route.path_coordinates?.length > 0 && editingRouteId !== route.id).map((route) => {
            const midpointIndex = Math.floor(route.path_coordinates.length / 2);
            const [lat, lng] = route.path_coordinates[midpointIndex];
            return (
              <LeafletMarker
                key={`label-${route.id}`}
                position={[lat, lng]}
                icon={L.divIcon({
                  className: 'route-label',
                  iconSize: [100, 20],
                  iconAnchor: [50, 10],
                  html: `<div style="background:${route.route_color};color:white;padding:2px 8px;border-radius:4px;font-size:11px;font-weight:600;white-space:nowrap;text-align:center;box-shadow:0 1px 3px rgba(0,0,0,0.3);">${route.route_name}</div>`,
                })}
              />
            );
          }) : null}

          {searchMarker ? (
            <LeafletMarker position={[searchMarker.lat, searchMarker.lng]} icon={L.icon({ iconUrl: '/leaflet/marker-icon-red.png', shadowUrl: '/leaflet/marker-shadow.png', iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41] })}>
              <Popup>
                <div className="p-1">
                  <p className="font-semibold text-sm" data-testid="search-result-popup-title">Search Result</p>
                  <p className="text-xs text-gray-600">{addressSearch}</p>
                </div>
              </Popup>
            </LeafletMarker>
          ) : null}

          <LeafletMapController onMapReady={(map) => { leafletMapRef.current = map; }} />
          <LeafletViewportTracker onViewportChange={onViewportChange} />
          <LeafletMapSync center={mapCenter} zoom={mapZoom} />
          <LeafletInvalidateSize trigger={activePanel} />
        </MapContainer>
      ) : (
        <div key={`gmap-${gmapEpoch}-${showPOI ? 'poi' : 'nopoi'}-${darkMode ? 'dark' : 'light'}`} style={{ width: '100%', height: '100%' }} data-testid="google-map-wrap">
          <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
            <GoogleMapComponent
              defaultCenter={mapCenter}
              defaultZoom={mapZoom}
              mapId={showPOI ? GOOGLE_MAPS_MAP_ID : GOOGLE_MAPS_MAP_ID_NO_POI}
              colorScheme={darkMode ? 'DARK' : 'LIGHT'}
              style={{ width: '100%', height: '100%' }}
              disableDefaultUI
              disableDoubleClickZoom={isDrawingZone || isDrawingRoute || isEditingShape}
              gestureHandling="greedy"
              zoomControl={false}
              mapTypeControl={false}
              scaleControl={false}
              streetViewControl={false}
              rotateControl={false}
              fullscreenControl={false}
              mapTypeId={mapType === 'satellite' ? 'satellite' : mapType === 'hybrid' ? 'hybrid' : mapType === 'terrain' ? 'terrain' : 'roadmap'}
              onClick={(event) => {
                if (isSelectingLocation && event.detail?.latLng) {
                  const lat = event.detail.latLng.lat;
                  const lng = event.detail.latLng.lng;
                  onPlaceChange((previous) => ({ ...previous, coordinates: [lat, lng] }));
                  onSelectingLocationChange(false);
                  toast.success('Location selected');
                  fetch(`${NOMINATIM_BASE}/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`)
                    .then((r) => r.ok ? r.json() : null)
                    .then((data) => {
                      if (!data) return;
                      const a = data.address;
                      const parts = [];
                      const street = [a?.house_number, a?.road].filter(Boolean).join(' ');
                      if (street) parts.push(street);
                      const suburb = a?.suburb || a?.village || a?.town || a?.hamlet;
                      if (suburb) parts.push(suburb);
                      if (a?.state) parts.push(a.state);
                      if (a?.postcode) parts.push(a.postcode);
                      if (a?.country) parts.push(a.country);
                      const addr = parts.length ? parts.join(', ') : data.display_name || '';
                      if (addr) onPlaceChange((prev) => ({ ...prev, address: addr }));
                    })
                    .catch(() => {});
                }
              }}
            >
              <GoogleMapController onMapReady={(map) => { mapRef.current = map; }} />
              <GoogleIdleListener onIdle={handleGoogleIdle} />

              {isDrawingZone || isDrawingRoute ? (
                <TerraDrawManager
                  drawingMode={
                    isDrawingZone && zoneForm.zone_type === 'polygon' ? 'polygon'
                      : isDrawingZone && zoneForm.zone_type === 'circle' ? 'circle'
                        : isDrawingZone && zoneForm.zone_type === 'rectangle' ? 'rectangle'
                          : isDrawingRoute ? 'linestring' : null
                  }
                  zoneColor={zoneForm.zone_color}
                  routeColor={routeForm.route_color}
                  onPolygonComplete={(coordinates) => {
                    onZoneGeometryChange({ type: 'polygon', coordinates });
                    onDrawingZoneChange(false);
                  }}
                  onCircleComplete={(center, radius) => {
                    onZoneGeometryChange({ type: 'circle', center, radius });
                    onDrawingZoneChange(false);
                  }}
                  onRectangleComplete={(coordinates) => {
                    onZoneGeometryChange({ type: 'rectangle', coordinates });
                    onDrawingZoneChange(false);
                  }}
                  onPolylineComplete={(coordinates) => {
                    onRoutePathChange(coordinates);
                    onDrawingRouteChange(false);
                  }}
                />
              ) : null}

              {activePanel === 'zone' && !isDrawingZone && zoneForm.geometry?.coordinates?.length >= 3 ? (
                <GooglePolygon
                  paths={zoneForm.geometry.coordinates.map((coord) => ({ lat: coord[0], lng: coord[1] }))}
                  options={{ fillColor: zoneForm.zone_color, fillOpacity: 0.3, strokeColor: zoneForm.zone_color, strokeWeight: 3, zIndex: 10, editable: zoneForm.zone_type !== 'region' && isEditingShape, draggable: zoneForm.zone_type !== 'region' && isEditingShape }}
                  onPathChanged={(paths) => {
                    if (!isEditingShape || zoneForm.zone_type === 'region') return;
                    onZoneGeometryChange({ type: zoneForm.geometry?.type || 'polygon', coordinates: paths.map((path) => [path.lat, path.lng]) });
                  }}
                />
              ) : null}
              {activePanel === 'zone' && !isDrawingZone && zoneForm.geometry?.type === 'circle' && zoneForm.geometry?.center ? (
                <GoogleCircle
                  center={{ lat: zoneForm.geometry.center[0], lng: zoneForm.geometry.center[1] }}
                  radius={zoneForm.geometry.radius || 500}
                  options={{ fillColor: zoneForm.zone_color, fillOpacity: 0.3, strokeColor: zoneForm.zone_color, strokeWeight: 3, zIndex: 10, editable: isEditingShape, draggable: isEditingShape }}
                  onChange={({ center, radius }) => {
                    if (!isEditingShape || !center) return;
                    onZoneGeometryChange({ type: 'circle', center: [center.lat, center.lng], radius });
                  }}
                />
              ) : null}
              {activePanel === 'place' && !isSelectingLocation && !isEditingShape && placeForm.coordinates?.length >= 2 ? (
                <GoogleCircle
                  center={{ lat: placeForm.coordinates[0], lng: placeForm.coordinates[1] }}
                  radius={placeForm.radius || 100}
                  options={{ fillColor: getPlaceColor(placeForm.icon), fillOpacity: 0.15, strokeColor: getPlaceColor(placeForm.icon), strokeWeight: 2, zIndex: 5, clickable: false }}
                />
              ) : null}
              {activePanel === 'place' && !isSelectingLocation && placeForm.coordinates?.length >= 2 && placeForm.icon ? (
                <AdvancedMarker position={{ lat: placeForm.coordinates[0], lng: placeForm.coordinates[1] }} anchorPoint={AdvancedMarkerAnchorPoint.CENTER}>
                  <PlaceMarkerContent icon={placeForm.icon} />
                </AdvancedMarker>
              ) : null}
              {activePanel === 'route' && !isDrawingRoute && !isEditingShape && previewRouteCorridorG ? previewRouteCorridorG.map((paths, i) => (
                <GooglePolygon key={`preview-corridor-${i}`} paths={paths} options={{ fillColor: routeForm.route_color, fillOpacity: 0.08, strokeColor: routeForm.route_color, strokeOpacity: 0.7, strokeWeight: 1.5, clickable: false, zIndex: 4 }} />
              )) : null}
              {activePanel === 'route' && !isDrawingRoute && routeForm.path_coordinates?.length >= 2 ? (
                <GooglePolyline
                  path={routeForm.path_coordinates.map((coord) => ({ lat: coord[0], lng: coord[1] }))}
                  options={{ strokeColor: routeForm.route_color, strokeWeight: 4, zIndex: 10, editable: isEditingShape, draggable: isEditingShape }}
                  onPathChanged={(paths) => {
                    if (!isEditingShape) return;
                    onRoutePathChange(paths.map((path) => [path.lat, path.lng]));
                  }}
                />
              ) : null}

              {showZonesLayer ? zones.filter((zone) => zone.visible_on_map && editingZoneId !== zone.id).map((zone) => {
                const { type, coords, center, radius } = getZoneCoords(zone);
                return type === 'circle' && center && Number.isFinite(center[0]) && Number.isFinite(center[1]) ? (
                  <GoogleCircle key={zone.id} center={{ lat: center[0], lng: center[1] }} radius={radius} options={{ fillColor: zone.zone_color, fillOpacity: selectedZone?.id === zone.id ? 0.4 : 0.2, strokeColor: zone.zone_color, strokeWeight: selectedZone?.id === zone.id ? 3 : 2, clickable: true }} onClick={() => onSelectZone(zone, { focus: false })} />
                ) : coords.length >= 3 ? (
                  <GooglePolygon key={zone.id} paths={coords.filter((coord) => Number.isFinite(coord?.[0]) && Number.isFinite(coord?.[1])).map((coord) => ({ lat: coord[0], lng: coord[1] }))} options={{ fillColor: zone.zone_color, fillOpacity: selectedZone?.id === zone.id ? 0.4 : 0.2, strokeColor: zone.zone_color, strokeWeight: selectedZone?.id === zone.id ? 3 : 2, clickable: true }} onClick={() => onSelectZone(zone, { focus: false })} />
                ) : null;
              }) : null}

              {showZonesLayer ? zones.filter((zone) => zone.visible_on_map && zone.show_name_on_map && editingZoneId !== zone.id).map((zone) => {
                const geometry = zone.geometry;
                let lat;
                let lng;
                if (geometry?.type === 'circle' && geometry.center) {
                  lat = geometry.center[0];
                  lng = geometry.center[1];
                } else if (geometry?.coordinates?.length > 0) {
                  lat = geometry.coordinates.reduce((sum, coord) => sum + coord[0], 0) / geometry.coordinates.length;
                  lng = geometry.coordinates.reduce((sum, coord) => sum + coord[1], 0) / geometry.coordinates.length;
                }
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                return (
                  <AdvancedMarker key={`label-${zone.id}`} position={{ lat, lng }}>
                    <div style={{ background: zone.zone_color, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', pointerEvents: 'none' }} data-testid={`zone-label-${zone.id}`}>
                      {zone.zone_name}
                    </div>
                  </AdvancedMarker>
                );
              }) : null}

              {showPlacesLayer ? places.filter((place) => place.visible_on_map && editingPlaceId !== place.id && Number.isFinite(place.coordinates?.[0]) && Number.isFinite(place.coordinates?.[1])).map((place) => (
                <GoogleCircle
                  key={`radius-${place.id}`}
                  center={{ lat: place.coordinates[0], lng: place.coordinates[1] }}
                  radius={place.radius || 100}
                  options={{ fillColor: getPlaceColor(place.icon), fillOpacity: selectedPlace?.id === place.id ? 0.25 : 0.12, strokeColor: getPlaceColor(place.icon), strokeWeight: 2, clickable: true }}
                  onClick={() => onSelectPlace(place, { focus: false })}
                />
              )) : null}

              {showPlacesLayer ? places.filter((place) => place.visible_on_map && place.icon && Number.isFinite(place.coordinates?.[0]) && Number.isFinite(place.coordinates?.[1])).map((place) => (
                <AdvancedMarker key={place.id} position={{ lat: place.coordinates[0], lng: place.coordinates[1] }} anchorPoint={AdvancedMarkerAnchorPoint.CENTER} onClick={() => { onInfoWindowOpenChange(place.id); onSelectPlace(place, { focus: false }); }}>
                  <PlaceMarkerContent icon={place.icon} />
                </AdvancedMarker>
              )) : null}

              {showPlacesLayer ? places.filter((place) => place.visible_on_map && place.show_name_on_map && editingPlaceId !== place.id && Number.isFinite(place.coordinates?.[0]) && Number.isFinite(place.coordinates?.[1])).map((place) => (
                <AdvancedMarker key={`label-${place.id}`} position={{ lat: place.coordinates[0], lng: place.coordinates[1] }}>
                  <div style={{ background: getPlaceColor(place.icon), color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', pointerEvents: 'none', transform: 'translateY(-24px)' }} data-testid={`place-label-${place.id}`}>
                    {place.place_name}
                  </div>
                </AdvancedMarker>
              )) : null}

              {showPlacesLayer ? places.filter((place) => infoWindowOpen === place.id && Number.isFinite(place.coordinates?.[0]) && Number.isFinite(place.coordinates?.[1])).map((place) => (
                <GInfoWindow key={`info-${place.id}`} position={{ lat: place.coordinates[0], lng: place.coordinates[1] }} onClose={() => onInfoWindowOpenChange(null)}>
                  <div className="p-1">
                    <p className="font-semibold text-sm" data-testid={`google-place-popup-name-${place.id}`}>{place.place_name}</p>
                    {place.address ? <p className="text-xs">{place.address}</p> : null}
                  </div>
                </GInfoWindow>
              )) : null}

              {showRoutesLayer ? routes.filter((route) => route.visible_on_map && editingRouteId !== route.id).flatMap((route) => (
                (routeCorridorsG[route.id] || []).map((paths, i) => (
                  <GooglePolygon key={`corridor-${route.id}-${i}`} paths={paths} options={{ fillColor: route.route_color, fillOpacity: selectedRoute?.id === route.id ? 0.14 : 0.08, strokeColor: route.route_color, strokeOpacity: 0.7, strokeWeight: 1.5, clickable: true, zIndex: -1 }} onClick={() => onSelectRoute(route, { focus: false })} />
                ))
              )) : null}

              {showRoutesLayer ? routes.filter((route) => route.visible_on_map && editingRouteId !== route.id).map((route) => (
                <GooglePolyline key={route.id} path={(route.path_coordinates || []).filter((waypoint) => Number.isFinite(waypoint?.[0]) && Number.isFinite(waypoint?.[1])).map((waypoint) => ({ lat: waypoint[0], lng: waypoint[1] }))} options={{ strokeColor: route.route_color, strokeWeight: selectedRoute?.id === route.id ? 5 : 3, strokeOpacity: selectedRoute?.id === route.id ? 1 : 0.7, clickable: true }} onClick={() => onSelectRoute(route, { focus: false })} />
              )) : null}

              {showRoutesLayer ? routes.filter((route) => route.visible_on_map && route.show_name_on_map && route.path_coordinates?.length > 0 && editingRouteId !== route.id).map((route) => {
                const midpointIndex = Math.floor(route.path_coordinates.length / 2);
                const [lat, lng] = route.path_coordinates[midpointIndex] || [];
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
                return (
                  <AdvancedMarker key={`label-${route.id}`} position={{ lat, lng }}>
                    <div style={{ background: route.route_color, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 1px 3px rgba(0,0,0,0.3)', pointerEvents: 'none' }} data-testid={`route-label-${route.id}`}>
                      {route.route_name}
                    </div>
                  </AdvancedMarker>
                );
              }) : null}

              {searchMarker ? (
                <AdvancedMarker position={{ lat: searchMarker.lat, lng: searchMarker.lng }} onClick={() => onInfoWindowOpenChange('search-marker')}>
                  <img src="/leaflet/marker-icon-red.png" alt="Search" style={{ width: '25px', height: '41px' }} data-testid="google-search-marker" />
                </AdvancedMarker>
              ) : null}
              {infoWindowOpen === 'search-marker' && searchMarker ? (
                <GInfoWindow position={{ lat: searchMarker.lat, lng: searchMarker.lng }} onClose={() => onInfoWindowOpenChange(null)}>
                  <div className="p-1">
                    <p className="font-semibold text-sm" data-testid="google-search-result-title">Search Result</p>
                    <p className="text-xs text-gray-600">{addressSearch}</p>
                  </div>
                </GInfoWindow>
              ) : null}
            </GoogleMapComponent>
          </APIProvider>
        </div>
      )}

      {legendCount > 0 ? (
        <div className="absolute bottom-3 right-3 z-[1000]" data-testid="map-legend">
          <div className="bg-white/95 dark:bg-slate-800/95 backdrop-blur-sm rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 overflow-hidden" style={{ maxWidth: '200px' }}>
            <button
              onClick={() => setLegendOpen((p) => !p)}
              className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-xs font-semibold text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors"
              type="button"
              data-testid="legend-toggle"
            >
              <span>Legend ({legendCount})</span>
              {legendOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
            </button>
            {legendOpen ? (
              <div className="border-t border-slate-200 dark:border-slate-700 px-3 py-2 space-y-1.5 max-h-48 overflow-y-auto" data-testid="legend-content">
                {legendZones.length > 0 ? (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-slate-400 mb-0.5">Zones</p>
                    {legendZones.map((z) => (
                      <div key={z.id} className="flex items-center gap-1.5 py-0.5" data-testid={`legend-zone-${z.id}`}>
                        <div className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: z.zone_color }} />
                        <span className="text-[11px] truncate text-slate-600 dark:text-slate-300">{z.zone_name}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                {legendPlaces.length > 0 ? (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-slate-400 mb-0.5">Places</p>
                    {legendPlaces.map((p) => (
                      <div key={p.id} className="flex items-center gap-1.5 py-0.5" data-testid={`legend-place-${p.id}`}>
                        {p.icon ? <img src={placeIconUrl(p.icon)} alt="" className="w-3.5 h-3.5 shrink-0" /> : <div className="w-2.5 h-2.5 rounded-full shrink-0 bg-emerald-500" />}
                        <span className="text-[11px] truncate text-slate-600 dark:text-slate-300">{p.place_name}</span>
                        <span className="text-[10px] shrink-0 ml-auto text-slate-400 dark:text-slate-500" data-testid={`legend-place-radius-${p.id}`}>{Math.round(p.radius || 100)}m</span>
                      </div>
                    ))}
                  </div>
                ) : null}
                {legendRoutes.length > 0 ? (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-slate-400 mb-0.5">Routes</p>
                    {legendRoutes.map((r) => (
                      <div key={r.id} className="flex items-center gap-1.5 py-0.5" data-testid={`legend-route-${r.id}`}>
                        <div className="w-4 h-0.5 shrink-0 rounded" style={{ backgroundColor: r.route_color }} />
                        <span className="text-[11px] truncate text-slate-600 dark:text-slate-300">{r.route_name}</span>
                        <span className="text-[10px] shrink-0 ml-auto text-slate-400 dark:text-slate-500" data-testid={`legend-route-deviation-${r.id}`}>±{Math.round(r.deviation_distance || 100)}m</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};